import { IComboBox, IComboBoxOption, IComboBoxStyles, IIconProps } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPIconButton, IPLabel, IPText } from 'baseComponents';
import { IPComboBox } from 'baseComponents/Combobox/IPComboBox';
import { IPStack } from 'baseComponents/Stack/IPStack';
import TextInfo from 'components/Notification/Info';
import { globalComboContainerHeight, newIcon } from 'globalStyles';
import StringValue, { StringValueTypes } from 'models/stringValue/stringValue';
import { StringValue_Translation } from 'models/stringValue/stringValue_Translation';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { apiAddStringValue, apiGetStringValues } from 'services/Api/stringValueService';
import { apiRequest } from 'services/Auth/authConfig';
import { AddStringValueModal } from './AddStringValueModal';
import CopilotMatchingStringValuesModal from './CopilotMatchingStringValuesModal';
import { useTranslation } from 'react-i18next';

interface IStringValueSelectProps {
  label?: string;
  info?: string;
  currentValueId?: number;
  emptyDisplayText?: string;
  setEmptyText?: string;
  setEmptyIcon?: IIconProps;
  stringValueType: StringValueTypes;
  canAddNew: boolean;
  onChange: (value: StringValue | undefined) => void;
  allowEmptyValue?: boolean;
  isReadOnly?: boolean;
}

const comboBoxStyles: Partial<IComboBoxStyles> = {
  ...globalComboContainerHeight,
};

const StringValueSelect = (props: IStringValueSelectProps) => {
  const appContext = useContext(AppContext);
  const comboBoxRef = useRef<IComboBox>(null);
  const { t } = useTranslation('stringvalue');
  const [addStringValueModalOpen, setAddStringValueModalOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedKey, setSelectedKey] = useState<string | number | null>(props.currentValueId ?? null);
  const [stringValues, setStringValues] = useState<StringValue[]>([]);
  const [stringValuesDisplay, setStringValuesDisplay] = useState<StringValue[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [matchingStringValues, setMatchingStringValues] = useState<StringValue[]>([]);
  const [showSelectMatchingStringValues, setShowSelectMatchingStringValues] = useState<StringValue | undefined>(
    undefined,
  );

  const currentValue: StringValue | undefined = stringValues.find((sv) => sv.stringValueId === props.currentValueId);

  const options: IComboBoxOption[] = stringValuesDisplay.map((sv) => ({
    key: sv.stringValueId,
    text: sv.value,
    title: sv.description,
  }));
  const ADD_NEW_OPTION_KEY = '__add_new__';
  const CLEAR_VALUE_OPTION_KEY = '__clear_value__';
  const actionOption: IComboBoxOption[] = props.setEmptyText
    ? [
        {
          key: CLEAR_VALUE_OPTION_KEY,
          text: props.setEmptyText,
        },
      ]
    : [];
  const comboBoxOptions: IComboBoxOption[] =
    props.canAddNew === false
      ? [...options, ...actionOption]
      : [
          ...options,
          ...actionOption,
          {
            key: ADD_NEW_OPTION_KEY,
            text: t('stringValue:AddStringValue.AddValue'),
          },
        ];

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const queryLower = searchQuery.toLowerCase();
    const filtered = stringValues.filter((entry) => {
      return Object.values(entry).some((val) => {
        if (typeof val === 'string') {
          if (entry.value && entry.value.toLowerCase().includes(queryLower)) {
            return true;
          }
          if (entry.description && entry.description.toLowerCase().includes(queryLower)) {
            return true;
          }
        }

        return false;
      });
    });
    if (filtered) {
      setStringValuesDisplay(filtered);
    }
  }, [stringValues, searchQuery]);

  useEffect(() => {
    setSelectedKey(props.currentValueId ?? null);
  }, [props.currentValueId]);

  const loadData = async () => {
    try {
      setIsLoading(true);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const getStringValues: StringValue[] = await apiGetStringValues(props.stringValueType, accessToken);

      setStringValues(getStringValues);
    } catch (err) {
      appContext.setError(err);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const onChange = (event: React.FormEvent<IComboBox>, option?: IComboBoxOption, index?: number, value?: string) => {
    if (!option) {
      if (props.allowEmptyValue && (!value || value.trim() === '')) {
        setSelectedKey(null);
        props.onChange(undefined);
      }

      return;
    }
    if (option.key === ADD_NEW_OPTION_KEY && props.canAddNew) {
      setAddStringValueModalOpen(true);
      setSelectedKey(null);

      return;
    }

    if (option.key === CLEAR_VALUE_OPTION_KEY) {
      setSelectedKey(null);
      setSearchQuery('');
      setMenuOpen(false);
      props.onChange(undefined);

      return;
    }

    setSelectedKey(option.key);
    const selected = stringValues.find((sv) => sv.stringValueId === option.key);
    if (selected) {
      props.onChange(selected);
    }
  };

  const createNewStringValue = async (newStringValue: StringValue, matchingStringValues: StringValue[] | undefined) => {
    try {
      if (matchingStringValues && matchingStringValues.length > 0) {
        setMatchingStringValues(matchingStringValues);
        setShowSelectMatchingStringValues(newStringValue);

        return;
      }

      setIsLoading(true);
      setAddStringValueModalOpen(false);

      newStringValue.stringTypeId = props.stringValueType;
      newStringValue.trans = [new StringValue_Translation()];
      newStringValue.transIdx = 0;
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const stringValue = await apiAddStringValue(newStringValue, accessToken);
      setStringValues((prev) => [...prev, stringValue]);
      props.onChange(stringValue);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const onRenderOption = (
    option?: IComboBoxOption,
    defaultRender?: (option?: IComboBoxOption) => JSX.Element | null,
  ): JSX.Element | null => {
    if (!option) return null;
    if (option.key === ADD_NEW_OPTION_KEY) {
      return (
        <IPStack horizontal verticalAlign="center">
          <IPIconButton iconProps={newIcon} style={{ marginLeft: -8 }} />
          <IPText>{option.text}</IPText>
        </IPStack>
      );
    } else if (option.key === CLEAR_VALUE_OPTION_KEY && props.setEmptyIcon) {
      return (
        <IPStack horizontal verticalAlign="center">
          <IPIconButton iconProps={props.setEmptyIcon} style={{ marginLeft: -8 }} />
          <IPText>{option.text}</IPText>
        </IPStack>
      );
    } else {
      return defaultRender ? defaultRender(option) : null;
    }
  };

  const onRenderLabel = (): JSX.Element | null => {
    if (!props || !props.label) return null;

    return (
      <TextInfo message={props.info}>
        <IPLabel>{props.label}</IPLabel>
      </TextInfo>
    );
  };

  // ComboBox does not provide a way to open the options when focusing into it, so we manage focus state ourselves (together with onClick() on the icon button)
  const [isFocused, setIsFocused] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const onFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const onBlur = useCallback(() => {
    setIsFocused(false);
    if (!currentValue) {
      setSearchQuery('');
    }
  }, [currentValue]);

  useEffect(() => {
    if (!comboBoxRef.current) return;
    if (isFocused) {
      comboBoxRef.current.focus(true);
    } else {
      comboBoxRef.current.dismissMenu();
    }
  }, [isFocused]);

  //
  // Main render
  //
  return (
    <IPStack>
      <IPComboBox
        componentRef={comboBoxRef}
        label={props.label}
        onRenderLabel={onRenderLabel}
        options={comboBoxOptions}
        selectedKey={selectedKey}
        allowFreeform={true}
        text={currentValue ? currentValue.value : searchQuery || props.emptyDisplayText}
        styles={comboBoxStyles}
        onChange={onChange}
        useComboBoxAsMenuWidth={true}
        onRenderOption={onRenderOption}
        onPendingValueChanged={(_option, _index, newValue) => {
          if (newValue) setSearchQuery(newValue);
        }}
        openOnKeyboardFocus={false}
        onBlur={onBlur}
        disabled={isLoading || props.isReadOnly}
        autoComplete="on"
        onFocus={onFocus}
        iconButtonProps={{
          onClick: (e) => {
            e.stopPropagation();
            if (menuOpen) {
              comboBoxRef.current?.dismissMenu();
              setMenuOpen(false);
            } else {
              comboBoxRef.current?.focus(true);
              setMenuOpen(true);
            }
          },
        }}
      />
      <AddStringValueModal
        isOpen={addStringValueModalOpen}
        onDismiss={() => {
          setAddStringValueModalOpen(false);
          setSelectedKey(null);
        }}
        stringValueType={props.stringValueType}
        onCreate={createNewStringValue}
      />
      {showSelectMatchingStringValues !== undefined && (
        <CopilotMatchingStringValuesModal
          isOpen={showSelectMatchingStringValues !== undefined}
          onClose={() => setShowSelectMatchingStringValues(undefined)}
          newStringValue={showSelectMatchingStringValues}
          matchingStringValues={matchingStringValues.map((sv) => sv.stringValueId)}
          existingStringValues={props.currentValueId ? [props.currentValueId] : []}
          onSelect={(selectedStringValue: StringValue, isNew: boolean) => {
            setShowSelectMatchingStringValues(undefined);
            if (isNew) {
              createNewStringValue(selectedStringValue, undefined);
            } else {
              props.onChange(selectedStringValue);
              setSelectedKey(selectedStringValue.stringValueId);
            }
          }}
        />
      )}
    </IPStack>
  );
};

export default StringValueSelect;
